class SubscriptionBuilderProductCard extends HTMLElement {
  constructor() {
    super();
    this.product = {};
    this.productType = null;
    this.productId = null;
    this.oneTimeId = null;
    this.inBundleQty = 0;
    this.initialized = false;
    this.clubPrice = 0;
    this._totalBundleSize = 0;
    this._clubMembershipToggleOn = false;
    this.location = 'subscription_builder';
    this.addressId = '';
    this.nextChargeScheduledAt = '';
    this.handleIncreaseQtyClick = null;
    this.handleDecreaseQtyClick = null;
    this.handleMoreInfoClick = null;
    this.session = null;
    this.debounce = this.debounce.bind(this);

    // this.isUpdating = false;
    this._isUpdating = false;
    this.onSubscriptionsLoaded = this.onSubscriptionsLoaded.bind(this);
  }

  debounce(func, delay) {
    let debounceTimer;
    return (...args) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(this, args), delay);
    };
  }

  set clubMembershipToggleOn(value) {
    this._clubMembershipToggleOn = value;
    this.updatePricing();
  }

  get clubMembershipToggleOn() {
    return this._clubMembershipToggleOn;
  }

  set isUpdating(value) {
    this._isUpdating = value;
    if (value) {
      this.setAttribute('is-updating', '');
    } else {
      this.removeAttribute('is-updating');
    }
    this.updateUI();
  }

  get isUpdating() {
    return this._isUpdating;
  }

  set totalBundleSize(value) {
    this._totalBundleSize = value;
    this.updatePricing();
    // console.log('Total bundle size:', this._totalBundleSize);
  }

  get totalBundleSize() {
    return this._totalBundleSize;
  }

  static get observedAttributes() {
    return ['in-bundle', 'address-id', 'next-charge-scheduled-at'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case 'in-bundle':
          this.inBundleQty = parseInt(newValue, 10) || 0;
          this.updateUI();
          break;
        case 'address-id':
          this.addressId = newValue || '';
          // Perform any additional actions needed when addressId changes
          break;
        case 'next-charge-scheduled-at':
          this.nextChargeScheduledAt = newValue || '';
          // Perform any additional actions needed when nextChargeScheduledAt changes
          break;
      }
    }
  }

  connectedCallback() {
    this.location = this.getAttribute('location') || 'subscription_builder';
    this.addressId = this.getAttribute('address-id') || '';
    this.nextChargeScheduledAt = this.getAttribute('next-charge-scheduled-at') || '';
    if (!this.initialized) {
      this.initialized = true;
      this.inBundleQty = parseInt(this.getAttribute('in-bundle'), 10) || 0;
      this.parseProductAttribute();
      if (this.productId) {
        if (this.location === 'onetime_builder') {
          this.handleIncreaseQtyClick = this.debounce(
            this.updateOnetimeQuantity.bind(this, 1),
            500
          );
          this.handleDecreaseQtyClick = this.debounce(
            this.updateOnetimeQuantity.bind(this, -1),
            500
          );
          this.handleMoreInfoClick = this.handleMoreInfoClickOnetime.bind(this);
        } else {
          this.handleIncreaseQtyClick = this.debounce(this.updateBundleQuantity.bind(this, 1), 300);
          this.handleDecreaseQtyClick = this.debounce(
            this.updateBundleQuantity.bind(this, -1),
            300
          );
          this.handleMoreInfoClick = this.handleMoreInfoClickSubscription.bind(this);
        }

        this.updateUI();
        this.attachEventListeners();
      } else {
        console.error('Product data not found or invalid.');
        this.displayError('Product information is unavailable.');
      }
    }
  }

  disconnectedCallback() {
    this.detachEventListeners();
  }

  parseProductAttribute() {
    const productData = this.getAttribute('product');
    if (productData) {
      try {
        this.product = JSON.parse(productData);
        this.productId = this.product.id;
        if (this.product.tags.includes('beer')) {
          this.productType = 'beer';
          this.clubPrice = 1099;
        } else if (this.product.tags.includes('seltzer')) {
          this.productType = 'seltzer';
          this.clubPrice = 899;
        }
      } catch (error) {
        console.error('Invalid JSON for product attribute:', error);
      }
    } else {
      console.error('Product data not found.');
    }
  }

  async updateOnetimeQuantity(change, event) {
    event.preventDefault();
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.oneTimeId = this.getAttribute('one-time-id');

    if (!this.session) {
      console.error('Session is not available. Cannot update onetime quantity.');
      return;
    }
    const oldQty = this.inBundleQty;
    const newQty = Math.max(0, this.inBundleQty + change);
    try {
      if (newQty == 1 && oldQty == 0) {
        //alert('need to add');

        const toSend = {
          address_id: parseInt(this.addressId),
          next_charge_scheduled_at: this.nextChargeScheduledAt,
          quantity: newQty,
          external_variant_id: {
            ecommerce: this.product.variants[0].id.toString(),
          },
          external_product_id: {
            ecommerce: this.product.id.toString(),
          },
        };
        await recharge.onetime.createOnetime(this.session, toSend);
      } else if (newQty > 0) {
        await recharge.onetime.updateOnetime(this.session, this.oneTimeId, {
          quantity: newQty,
        });
      } else {
        await recharge.onetime.deleteOnetime(this.session, this.oneTimeId);
      }
      if (change > 0) {
        triggerToast({
          title: 'Success',
          message: 'Item Added',
          status: 'success',
          position: 'top-right',
          size: 'small',
          duration: 3000,
        });
      } else {
        triggerToast({
          title: 'Success',
          message: 'Item Removed',
          status: 'success',
          position: 'top-right',
          size: 'small',
          duration: 3000,
        });
      }

      const event = new CustomEvent('closeOnetimesPanel', {
        bubbles: true,
        composed: true,
      });

      window.dispatchEvent(event);
    } catch (error) {
      triggerToast({
        title: 'Error',
        message:
          'There was an error updating your subscription.  Please refresh the page and try again.',
        status: 'error',
        position: 'top-right',
        size: 'large',
        duration: 5000,
      });
      console.error('Failed to update onetime product quantity:', error);
    } finally {
      const customerPortal = document.querySelector('customer-portal');
      if (customerPortal) {
        await customerPortal.getSubscriptions(true);
      }
      this.isUpdating = false;
    }
  }
  handleMoreInfoClickOnetime(event) {
    event.preventDefault();
    const moreInfoPanel = document.querySelector('more-info-panel');
    if (!moreInfoPanel) return;
    moreInfoPanel.updateProperties({
      productHandle: this.product.handle,
    });

    moreInfoPanel
      .loadMainProduct()
      .then(() => {
        moreInfoPanel.open(this);
      })
      .catch((err) => {
        console.error('Error loading product info:', err);
      });
  }

  handleMoreInfoClickSubscription(event) {
    event.preventDefault();
    const moreInfoPanel = document.querySelector('more-info-panel');
    if (!moreInfoPanel) return;
    moreInfoPanel.updateProperties({
      productHandle: this.product.handle,
    });

    moreInfoPanel
      .loadMainProduct()
      .then(() => {
        moreInfoPanel.open(this);
      })
      .catch((err) => {
        console.error('Error loading product info:', err);
      });
  }

  updateBundleQuantity(change, event) {
    event.preventDefault();
    const oldQty = this.inBundleQty;
    this.inBundleQty = Math.max(0, this.inBundleQty + change);

    /*
    commented out to remove limit on brew of the month
    if (this.product.tags.includes('LTO-SUBSCRIPTION')) {
      if (this.inBundleQty > 2) {
        this.inBundleQty = oldQty;
        triggerToast({
          title: 'Limit Reached',
          message: 'You can only add 2 of this item to your subscription.',
          status: 'info',
          position: 'top-right',
          size: 'large',
          duration: 5000,
        });
        return true;
      }
    }
*/

    this.updateUI();

    const eventDetail = { product: this.product, quantity: this.inBundleQty };
    const eventName = change > 0 ? 'add_item_to_bundle' : 'remove_item_from_bundle';

    if (oldQty === 0 && this.inBundleQty > 0) {
      this.dispatchEvent(
        new CustomEvent('add_item_to_bundle', {
          detail: eventDetail,
          bubbles: true,
          composed: true,
        })
      );
    } else if (oldQty > 0 && this.inBundleQty === 0) {
      this.dispatchEvent(
        new CustomEvent('remove_item_from_bundle', {
          detail: eventDetail,
          bubbles: true,
          composed: true,
        })
      );
    } else {
      this.dispatchEvent(
        new CustomEvent(eventName, { detail: eventDetail, bubbles: true, composed: true })
      );
    }
  }

  handleRechargeInitialized(event) {
    // console.log('Recharge initialized:', event.detail);
    this.session = event.detail.session;
    // console.log('this.session:', this.session);
  }

  handleMoreInfoClick(event) {
    event.preventDefault();
    console.log(`More info about product: ${this.product.title}`, this.product);
    const moreInfoPanel = document.querySelector('more-info-panel');
    if (!moreInfoPanel) return;
    moreInfoPanel.updateProperties({
      productHandle: this.product.handle,
    });

    moreInfoPanel
      .loadMainProduct()
      .then(() => {
        moreInfoPanel.open(this);
      })
      .catch((err) => {
        console.error('Error loading product info:', err);
      });
  }

  attachEventListeners() {
    window.addEventListener('rechargeInitialized', this.handleRechargeInitialized.bind(this));
    window.addEventListener('subscriptionsLoaded', this.onSubscriptionsLoaded);

    this.addButton = this.querySelector('.sb_atc_btn');
    if (this.addButton) this.addButton.addEventListener('click', this.handleIncreaseQtyClick);

    this.increaseQtyButton = this.querySelector('.sb_qty_change_btn_plus');
    if (this.increaseQtyButton)
      this.increaseQtyButton.addEventListener('click', this.handleIncreaseQtyClick);

    this.decreaseQtyButton = this.querySelector('.sb_qty_change_btn_minus');
    if (this.decreaseQtyButton)
      this.decreaseQtyButton.addEventListener('click', this.handleDecreaseQtyClick);

    this.moreInfoButton = this.querySelector('.more_info_button');
    if (this.moreInfoButton)
      this.moreInfoButton.addEventListener('click', this.handleMoreInfoClick);
  }

  detachEventListeners() {
    window.removeEventListener('rechargeInitialized', this.handleRechargeInitialized.bind(this));
    window.removeEventListener('subscriptionsLoaded', this.onSubscriptionsLoaded);

    if (this.addButton) this.addButton.removeEventListener('click', this.handleIncreaseQtyClick);
    if (this.increaseQtyButton)
      this.increaseQtyButton.removeEventListener('click', this.handleIncreaseQtyClick);
    if (this.decreaseQtyButton)
      this.decreaseQtyButton.removeEventListener('click', this.handleDecreaseQtyClick);
    if (this.moreInfoButton)
      this.moreInfoButton.removeEventListener('click', this.handleMoreInfoClick);
  }

  onSubscriptionsLoaded(event) {
    const { subscriptions, charges, oneTimes } = event.detail;
    //console.log('Subscriptions loaded on product-card:', event.detail);
    if (subscriptions) {
      this.nextChargeScheduledAt = subscriptions.currentSubscription?.next_charge_scheduled_at;
    }
  }

  updateUI() {
    this.detachEventListeners();
    const cardCta = this.querySelector('.card__cta');
    if (!cardCta) {
      console.error('card__cta element not found.');
      return;
    }

    cardCta.innerHTML = '';
    this.createMoreInfoButton(cardCta);
    if (this.isUpdating) {
      this.createSavingButton(cardCta);
    } else if (this.inBundleQty === 0) {
      this.createAddButton(cardCta);
    } else {
      this.createQuantityControls(cardCta);
    }

    this.attachEventListeners();
  }

  createAddButton(container) {
    const addButton = document.createElement('button');
    addButton.className = 'button button--primary sb_atc_btn';
    addButton.setAttribute('data-product-id', this.productId);
    addButton.setAttribute('aria-label', `Add ${this.product.title} to bundle`);
    addButton.innerText = 'Add';
    container.appendChild(addButton);
    this.addButton = addButton;
  }

  createQuantityControls(container) {
    const qtyBtnsDiv = document.createElement('div');
    qtyBtnsDiv.className = 'qty_btns';

    const decreaseQtyButton = document.createElement('button');
    decreaseQtyButton.className = 'button sb_qty_change_btn sb_qty_change_btn_minus';
    decreaseQtyButton.setAttribute(
      'aria-label',
      `Decrease quantity of ${this.product.title} in bundle`
    );
    decreaseQtyButton.innerText = '-';

    const qtyAmount = document.createElement('div');
    qtyAmount.className = 'sb_qty_amt';
    qtyAmount.setAttribute('aria-live', 'polite');
    qtyAmount.innerText = this.inBundleQty;

    const increaseQtyButton = document.createElement('button');
    increaseQtyButton.className = 'button sb_qty_change_btn sb_qty_change_btn_plus';
    increaseQtyButton.setAttribute(
      'aria-label',
      `Increase quantity of ${this.product.title} in bundle`
    );
    increaseQtyButton.innerText = '+';

    qtyBtnsDiv.append(decreaseQtyButton, qtyAmount, increaseQtyButton);
    container.appendChild(qtyBtnsDiv);

    this.decreaseQtyButton = decreaseQtyButton;
    this.increaseQtyButton = increaseQtyButton;
  }
  createSavingButton(container) {
    const savingButton = document.createElement('button');
    savingButton.className = 'button button--primary sb_saving_btn';
    savingButton.setAttribute('aria-label', `Saving ${this.product.title}...`);
    savingButton.innerText = 'Saving';
    savingButton.disabled = true;
    container.appendChild(savingButton);
    this.savingButton = savingButton;
  }
  createMoreInfoButton(container) {
    const moreInfoButton = document.createElement('button');
    moreInfoButton.className = 'link more_info_button';
    moreInfoButton.setAttribute('data-product-id', this.productId);
    moreInfoButton.setAttribute('aria-label', `More Info for ${this.product.title}`);
    const innerTextSVG = ` <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 16C5.87469 16 3.82957 15.16 2.34586 13.64C-0.781955 10.52 -0.781955 5.44 2.34586 2.32C3.82957 0.84 5.87469 0 8 0C10.1253 0 12.1704 0.84 13.6541 2.36C16.782 5.48 16.782 10.56 13.6541 13.68C12.1704 15.16 10.1253 16 8 16ZM8 0.8C6.07519 0.8 4.27068 1.56 2.90727 2.92C0.100251 5.72 0.100251 10.28 2.90727 13.12C4.27068 14.44 6.07519 15.2 8 15.2C9.92481 15.2 11.7293 14.44 13.0927 13.08C15.8997 10.28 15.8997 5.72 13.0927 2.88C11.7293 1.56 9.92481 0.8 8 0.8ZM8.6015 5C8.4411 5.16 8.2807 5.2 8.0802 5.2C7.8797 5.2 7.6792 5.12 7.5589 5C7.3985 4.84 7.3584 4.68 7.3584 4.48C7.3584 4.28 7.4386 4.08 7.5589 3.96C7.7193 3.8 7.8797 3.76 8.0802 3.76C8.2807 3.76 8.4411 3.84 8.6015 3.96C8.7619 4.12 8.802 4.28 8.802 4.48C8.802 4.68 8.7619 4.88 8.6015 5ZM7.5188 11.84V6.44C7.5188 6.36 7.599 6.28 7.6792 6.28H8.4812C8.5614 6.28 8.6416 6.36 8.6416 6.44V11.88C8.6416 11.96 8.5614 12.04 8.4812 12.04H7.6792C7.599 12 7.5188 11.92 7.5188 11.84Z"
            fill="#003A5D" />
        </svg>`;
    moreInfoButton.innerHTML = `
        ${innerTextSVG}
        <span class="more-info-text">More Info</span>
      `;
    container.appendChild(moreInfoButton);
    this.moreInfoButton = moreInfoButton;
  }

  updatePricing() {
    const priceContainer = this.querySelector('.price');
    const priceRegularContainer = this.querySelector('.price__regular');
    const priceSaleContainer = this.querySelector('.price__sale');

    if (!priceContainer || !priceRegularContainer || !priceSaleContainer) {
      console.error('Price containers not found');
      return;
    }

    const originalPrice = parseFloat(this.product.price);
    const discountUtils = window.__abc?.subscriptionDiscount;
    const salePrice = discountUtils
      ? discountUtils.calculateLineDiscountedPrice(this.product, this.totalBundleSize, {
          clubMembershipToggleOn: this.clubMembershipToggleOn,
          clubPrice: this.clubPrice,
        })
      : null;

    if (salePrice !== null && salePrice < originalPrice) {
      this.showSalePrice(salePrice, originalPrice);
    } else {
      this.showRegularPrice(originalPrice);
    }
  }

  showSalePrice(salePrice, originalPrice) {
    const priceContainer = this.querySelector('.price');
    const priceSaleContainer = this.querySelector('.price__sale');
    const clubBadge = this.querySelector('.card_price_club_badge');

    priceContainer.classList.add('price--on-sale');

    if (clubBadge) {
      if (this.clubMembershipToggleOn && this.totalBundleSize > 3) {
        clubBadge.style.display = 'block';
      } else {
        clubBadge.style.display = 'none';
      }
    }
    const salePriceElement = priceSaleContainer.querySelector('.price-item.price-item--sale');
    const originalPriceElement = priceSaleContainer.querySelector(
      's.price-item.price-item--regular'
    );

    if (salePriceElement) {
      salePriceElement.innerText = this.formatPrice(salePrice);
    }

    if (originalPriceElement) {
      originalPriceElement.innerText = this.formatPrice(originalPrice);
    }
  }

  showRegularPrice(regularPrice) {
    const priceContainer = this.querySelector('.price');
    const priceRegularContainer = this.querySelector('.price__regular');

    priceContainer.classList.remove('price--on-sale');

    const regularPriceElement = priceRegularContainer.querySelector(
      '.price-item.price-item--regular'
    );

    if (regularPriceElement) {
      regularPriceElement.innerText = this.formatPrice(regularPrice);
    }
  }

  formatPrice(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  getSession() {
    const session = sessionStorage.getItem('abc_recharge_session');
    return session ? JSON.parse(session) : null;
  }
}

customElements.define('subscription-builder-product-card', SubscriptionBuilderProductCard);
